// manufacturing/RawMaterialPurchase.jsx
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { setToogleHeader } from '../../core/redux/action';
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import Select from 'react-select';
import {
    Plus, Search, RotateCcw, Eye, Trash2, Printer, ChevronUp,
    ShoppingCart, Calendar, Package, DollarSign, Truck, AlertCircle,

} from 'feather-icons-react/build/IconComponents';
import { rawMaterialAPI, supplierAPI } from "../../core/action/api";
import { manufacturingActions } from "../../core/action/manufacturingAction";
import ImageWithBasePath from '../../core/img/imagewithbasebath';

const MySwal = withReactContent(Swal);

const RawMaterialPurchase = () => {
    const dispatch = useDispatch();
    const data = useSelector((state) => state.toggle_header);

    // State management
    const [purchases, setPurchases] = useState([]);
    const [suppliers, setSuppliers] = useState([]);
    const [rawMaterials, setRawMaterials] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedRows, setSelectedRows] = useState([]);

    // Modal states
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showViewModal, setShowViewModal] = useState(false);
    const [selectedPurchase, setSelectedPurchase] = useState(null);
    const [viewLoading, setViewLoading] = useState(false);

    // Form states
    const [formData, setFormData] = useState({
        purchase_date: new Date().toISOString().split('T')[0],
        supplier_id: '',
        invoice_no: '',
        reference_no: '',
        notes: '',
        items: []
    });

    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);

    // Filters
    const [filters, setFilters] = useState({
        search: '',
        supplier_id: '',
        from_date: '',
        to_date: ''
    });

    // Pagination
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 1
    });

    const canCreate = manufacturingActions.hasManufacturingPermission('create-raw-material-purchase');

    // Load data on mount
    useEffect(() => {
        loadPurchases();
        loadSuppliers();
        loadRawMaterials();
    }, []);

    // Load purchases
    const loadPurchases = async (page = pagination.page) => {
        setLoading(true);
        try {
            const params = {
                page,
                limit: pagination.limit,
                search: filters.search,
                supplier_id: filters.supplier_id,
                from_date: filters.from_date,
                to_date: filters.to_date
            };
            const response = await rawMaterialAPI.getPurchases(params);
            console.log('Purchases response:', response);

            if (response?.success) {
                setPurchases(response.data || []);
                setPagination({
                    page: response.page || page,
                    limit: pagination.limit,
                    total: response.total || 0,
                    totalPages: response.total_pages || 1
                });
            } else {
                toast.error(response?.message || 'Failed to load purchases');
            }
        } catch (error) {
            console.error('Error loading purchases:', error);
            toast.error('Failed to load purchases');
        } finally {
            setLoading(false);
        }
    };

    // Load suppliers
    const loadSuppliers = async () => {
        try {
            const response = await supplierAPI.getAll({ limit: 1000 });
            if (response?.success && response?.data) {
                setSuppliers(response.data);
            } else {
                // Fallback suppliers if API fails
                setSuppliers([]);
            }
        } catch (error) {
            console.error('Error loading suppliers:', error);
            setSuppliers([]);
        }
    };

    // Load raw materials for selection
    const loadRawMaterials = async () => {
        try {
            const response = await rawMaterialAPI.getAll({ limit: 1000, status: 'active' });
            console.log('Raw materials response:', response);

            if (response?.success && response?.data) {
                setRawMaterials(response.data);
            } else {
                setRawMaterials([]);
            }
        } catch (error) {
            console.error('Error loading raw materials:', error);
            setRawMaterials([]);
        }
    };

    // Handle filter change
    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
        setTimeout(() => loadPurchases(1), 100);
    };

    // Handle input change
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    // Handle select change
    const handleSelectChange = (selected, name) => {
        setFormData(prev => ({ ...prev, [name]: selected?.value || '' }));
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    // Add raw material to purchase
    const handleAddMaterial = () => {
        setFormData(prev => ({
            ...prev,
            items: [...prev.items, {
                id: Date.now(),
                material_id: '',
                material_name: '',
                sku: '',
                quantity: 0,
                unit: 'PCS',
                unit_price: 0,
                total_price: 0,
                remarks: ''
            }]
        }));
    };

    // Remove material from purchase
    const handleRemoveMaterial = (index) => {
        setFormData(prev => ({
            ...prev,
            items: prev.items.filter((_, i) => i !== index)
        }));
    };

    // Handle raw material selection
    const handleMaterialSelect = (index, selectedOption) => {
        if (!selectedOption) return;

        const material = selectedOption.data;

        if (material) {
            const updatedItems = [...formData.items];
            updatedItems[index] = {
                ...updatedItems[index],
                material_id: material.id,
                material_name: material.material_name,
                sku: material.sku,
                quantity: 0,
                unit: material.unit_name || 'PCS',
                unit_price: material.cost_price || 0,
                total_price: 0,
                remarks: ''
            };
            setFormData(prev => ({ ...prev, items: updatedItems }));
        }
    };

    // Handle quantity change
    const handleQuantityChange = (index, quantity) => {
        const updatedItems = [...formData.items];
        const qty = parseFloat(quantity) || 0;
        updatedItems[index].quantity = qty;
        updatedItems[index].total_price = (updatedItems[index].unit_price || 0) * qty;
        setFormData(prev => ({ ...prev, items: updatedItems }));

        if (errors[`quantity_${index}`]) {
            setErrors(prev => ({ ...prev, [`quantity_${index}`]: '' }));
        }
    };

    // Handle unit price change
    const handleUnitPriceChange = (index, price) => {
        const updatedItems = [...formData.items];
        const unitPrice = parseFloat(price) || 0;
        updatedItems[index].unit_price = unitPrice;
        updatedItems[index].total_price = unitPrice * (updatedItems[index].quantity || 0);
        setFormData(prev => ({ ...prev, items: updatedItems }));
    };

    // Validate form
    const validateForm = () => {
        const newErrors = {};

        if (!formData.supplier_id) {
            newErrors.supplier_id = 'Supplier is required';
        }

        if (formData.items.length === 0) {
            newErrors.items = 'At least one raw material is required';
        }

        let hasValidItem = false;
        for (let i = 0; i < formData.items.length; i++) {
            const item = formData.items[i];
            if (!item.material_id) {
                newErrors[`material_${i}`] = 'Raw material selection is required';
            }
            if (item.quantity <= 0) {
                newErrors[`quantity_${i}`] = 'Quantity must be greater than 0';
            } else {
                hasValidItem = true;
            }
            if (item.unit_price <= 0) {
                newErrors[`price_${i}`] = 'Unit price must be greater than 0';
            }
        }

        if (!hasValidItem && formData.items.length > 0) {
            newErrors.items = 'At least one valid item with quantity > 0 is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Handle form submission
    const handleSubmit = async () => {
        if (!validateForm()) {
            toast.error('Please fix the errors in the form');
            return;
        }

        setSubmitting(true);

        const dataToSend = {
            purchase_date: formData.purchase_date,
            supplier_id: formData.supplier_id,
            invoice_no: formData.invoice_no || null,
            reference_no: formData.reference_no || null,
            notes: formData.notes || null,
            items: formData.items
                .filter(item => item.material_id && item.quantity > 0)
                .map(item => ({
                    material_id: item.material_id,
                    quantity: item.quantity,
                    unit: item.unit,
                    unit_price: item.unit_price,
                    remarks: item.remarks || null
                }))
        };

        if (dataToSend.items.length === 0) {
            toast.error('No valid items to purchase');
            setSubmitting(false);
            return;
        }

        try {
            const result = await rawMaterialAPI.createPurchase(dataToSend);
            console.log('Create purchase result:', result);

            if (result && result.success) {
                toast.success(result.message || 'Raw material purchased successfully');
                resetForm();
                setShowCreateModal(false);
                await loadPurchases(1);
                await loadRawMaterials(); // Refresh raw materials list

                MySwal.fire({
                    icon: 'success',
                    title: 'Success!',
                    text: result.message || 'Raw material purchased successfully',
                    timer: 2000,
                    showConfirmButton: false
                });
            } else {
                const errorMsg = result?.message || 'Failed to create purchase';
                toast.error(errorMsg);
                setErrors({ form: errorMsg });
            }
        } catch (error) {
            console.error('Error creating purchase:', error);
            const errorMsg = error.response?.data?.message || error.message || 'Failed to create purchase';
            toast.error(errorMsg);
            setErrors({ form: errorMsg });
        } finally {
            setSubmitting(false);
        }
    };

    // Reset form
    const resetForm = () => {
        setFormData({
            purchase_date: new Date().toISOString().split('T')[0],
            supplier_id: '',
            invoice_no: '',
            reference_no: '',
            notes: '',
            items: []
        });
        setErrors({});
    };

    // View purchase details - FIXED
    const handleViewPurchase = async (purchase) => {
        console.log('Viewing purchase:', purchase);
        setViewLoading(true);
        setShowViewModal(true);

        try {
            // Fetch full purchase details including items
            const response = await rawMaterialAPI.getPurchase(purchase.id);
            console.log('Purchase details response:', response);

            if (response?.success && response?.data) {
                setSelectedPurchase(response.data);
            } else {
                toast.error(response?.message || 'Failed to load purchase details');
                setShowViewModal(false);
            }
        } catch (error) {
            console.error('Error loading purchase details:', error);
            toast.error('Failed to load purchase details');
            setShowViewModal(false);
        } finally {
            setViewLoading(false);
        }
    };

    // Format date
    const formatDate = (date) => {
        if (!date) return 'N/A';
        return new Date(date).toLocaleDateString('en-IN', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    // Format currency
    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 2
        }).format(amount || 0);
    };

    // Supplier options
    const supplierOptions = suppliers.map(supplier => ({
        value: supplier.id,
        label: `${supplier.name}${supplier.gst_no ? ` (${supplier.gst_no})` : ''}`,
        data: supplier
    }));

    // Raw material options for dropdown
    const rawMaterialOptions = rawMaterials.map(material => ({
        value: material.id,
        label: `${material.material_name} (${material.sku}) - Current Stock: ${material.quantity || 0} ${material.unit_name || 'PCS'}`,
        data: material
    }));

    // Calculate totals for create modal
    const calculateTotals = () => {
        const totalItems = formData.items.length;
        const totalQuantity = formData.items.reduce((sum, item) => sum + (item.quantity || 0), 0);
        const totalValue = formData.items.reduce((sum, item) => sum + (item.total_price || 0), 0);
        return { totalItems, totalQuantity, totalValue };
    };

    const totals = calculateTotals();

    const renderTooltip = (props, text) => (
        <Tooltip id={`tooltip-${text}`} {...props}>{text}</Tooltip>
    );

    return (
        <div className="page-wrapper">
            <div className="content">
                {/* Header */}
                <div className="page-header">
                    <div className="page-title">
                        <h4>Raw Material Purchase</h4>
                        <h6>Purchase raw materials from suppliers</h6>
                    </div>

                    <ul className="table-top-head">
                        <li>
                            <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'PDF')}>
                                <Link to="#">
                                    <ImageWithBasePath src="assets/img/icons/pdf.svg" alt="PDF" />
                                </Link>
                            </OverlayTrigger>
                        </li>
                        <li>
                            <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'Excel')}>
                                <Link to="#">
                                    <ImageWithBasePath src="assets/img/icons/excel.svg" alt="Excel" />
                                </Link>
                            </OverlayTrigger>
                        </li>
                        <li>
                            <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'Print')}>
                                <Link onClick={() => window.print()}>
                                    <Printer />
                                </Link>
                            </OverlayTrigger>
                        </li>
                        <li>
                            <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'Refresh')}>
                                <Link onClick={() => { loadPurchases(); loadRawMaterials(); }}>
                                    <RotateCcw />
                                </Link>
                            </OverlayTrigger>
                        </li>
                        <li>
                            <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'Collapse')}>
                                <Link
                                    className={data ? "active" : ""}
                                    onClick={() => dispatch(setToogleHeader(!data))}
                                >
                                    <ChevronUp />
                                </Link>
                            </OverlayTrigger>
                        </li>
                    </ul>

                    {canCreate && (
                        <div className="page-btn">
                            <button className="btn btn-primary" onClick={() => setShowCreateModal(true)}>
                                <Plus size={18} className="me-2" />
                                Purchase Raw Material
                            </button>
                        </div>
                    )}
                </div>

                {/* Statistics Cards */}
                <div className="row">
                    <div className="col-lg-3 col-md-6 col-sm-12">
                        <div className="card">
                            <div className="card-body">
                                <div className="d-flex align-items-center">
                                    <div className="bg-primary bg-opacity-10 p-3 rounded me-3">
                                        <ShoppingCart size={24} className="text-primary" />
                                    </div>
                                    <div>
                                        <p className="text-muted mb-1">Total Purchases</p>
                                        <h3 className="mb-0">{pagination.total}</h3>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-lg-3 col-md-6 col-sm-12">
                        <div className="card">
                            <div className="card-body">
                                <div className="d-flex align-items-center">
                                    <div className="bg-success bg-opacity-10 p-3 rounded me-3">
                                        <Package size={24} className="text-success" />
                                    </div>
                                    <div>
                                        <p className="text-muted mb-1">Total Items</p>
                                        <h3 className="mb-0">
                                            {purchases.reduce((sum, p) => sum + (p.total_items || 0), 0)}
                                        </h3>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-lg-3 col-md-6 col-sm-12">
                        <div className="card">
                            <div className="card-body">
                                <div className="d-flex align-items-center">
                                    <div className="bg-warning bg-opacity-10 p-3 rounded me-3">
                                        <DollarSign size={24} className="text-warning" />
                                    </div>
                                    <div>
                                        <p className="text-muted mb-1">Total Value</p>
                                        <h3 className="mb-0">
                                            {formatCurrency(purchases.reduce((sum, p) => sum + (parseFloat(p.total_value) || 0), 0))}
                                        </h3>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-lg-3 col-md-6 col-sm-12">
                        <div className="card">
                            <div className="card-body">
                                <div className="d-flex align-items-center">
                                    <div className="bg-info bg-opacity-10 p-3 rounded me-3">
                                        <Truck size={24} className="text-info" />
                                    </div>
                                    <div>
                                        <p className="text-muted mb-1">Total Suppliers</p>
                                        <h3 className="mb-0">{new Set(purchases.map(p => p.supplier_id)).size}</h3>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Filters */}
                <div className="card mb-4">
                    <div className="card-body">
                        <div className="row">
                            <div className="col-lg-4">
                                <div className="mb-3">
                                    <label>Search</label>
                                    <div className="input-group">
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="PO No, Invoice No..."
                                            value={filters.search}
                                            onChange={(e) => handleFilterChange('search', e.target.value)}
                                            onKeyPress={(e) => e.key === 'Enter' && loadPurchases(1)}
                                        />
                                        <button className="btn btn-primary" onClick={() => loadPurchases(1)}>
                                            <Search size={16} />
                                        </button>
                                    </div>
                                </div>
                            </div>
                            <div className="col-lg-3">
                                <div className="mb-3">
                                    <label>Supplier</label>
                                    <Select
                                        options={supplierOptions}
                                        isClearable
                                        placeholder="All Suppliers"
                                        onChange={(selected) => handleFilterChange('supplier_id', selected?.value || '')}
                                    />
                                </div>
                            </div>
                            <div className="col-lg-2">
                                <div className="mb-3">
                                    <label>From Date</label>
                                    <input
                                        type="date"
                                        className="form-control"
                                        value={filters.from_date}
                                        onChange={(e) => handleFilterChange('from_date', e.target.value)}
                                    />
                                </div>
                            </div>
                            <div className="col-lg-2">
                                <div className="mb-3">
                                    <label>To Date</label>
                                    <input
                                        type="date"
                                        className="form-control"
                                        value={filters.to_date}
                                        onChange={(e) => handleFilterChange('to_date', e.target.value)}
                                    />
                                </div>
                            </div>
                            <div className="col-lg-1">
                                <div className="mb-3">
                                    <label>&nbsp;</label>
                                    <button className="btn btn-secondary w-100" onClick={() => {
                                        setFilters({ search: '', supplier_id: '', from_date: '', to_date: '' });
                                        loadPurchases(1);
                                    }}>
                                        Clear
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Purchases Table */}
                <div className="card">
                    <div className="card-body">
                        {loading ? (
                            <div className="text-center py-5">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                            </div>
                        ) : purchases.length === 0 ? (
                            <div className="text-center py-5">
                                <ShoppingCart size={48} className="text-muted mb-3" />
                                <h5>No purchases found</h5>
                                <p className="text-muted">Purchase raw materials to start manufacturing</p>
                                {canCreate && (
                                    <button className="btn btn-primary" onClick={() => setShowCreateModal(true)}>
                                        <Plus size={16} className="me-2" />
                                        Purchase Raw Material
                                    </button>
                                )}
                            </div>
                        ) : (
                            <div className="table-responsive">
                                <table className="table table-hover">
                                    <thead>
                                        <tr>
                                            <th width="40px">
                                                <input
                                                    type="checkbox"
                                                    className="form-check-input"
                                                    checked={selectedRows.length === purchases.length}
                                                    onChange={(e) => {
                                                        if (e.target.checked) {
                                                            setSelectedRows(purchases.map(p => p.id));
                                                        } else {
                                                            setSelectedRows([]);
                                                        }
                                                    }}
                                                />
                                            </th>
                                            <th>PO No</th>
                                            <th>Date</th>
                                            <th>Supplier</th>
                                            <th>Invoice No</th>
                                            <th>Items</th>
                                            <th>Total Qty</th>
                                            <th>Total Value</th>
                                            <th className="text-center">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {purchases.map((purchase) => (
                                            <tr key={purchase.id}>
                                                <td className="ps-3">
                                                    <input
                                                        type="checkbox"
                                                        className="form-check-input"
                                                        checked={selectedRows.includes(purchase.id)}
                                                        onChange={(e) => {
                                                            if (e.target.checked) {
                                                                setSelectedRows([...selectedRows, purchase.id]);
                                                            } else {
                                                                setSelectedRows(selectedRows.filter(id => id !== purchase.id));
                                                            }
                                                        }}
                                                    />
                                                </td>
                                                <td>
                                                    <strong>{purchase.po_no}</strong>
                                                    {purchase.reference_no && (
                                                        <div className="small text-muted">Ref: {purchase.reference_no}</div>
                                                    )}
                                                </td>
                                                <td>
                                                    <div className="d-flex align-items-center">
                                                        <Calendar size={14} className="me-1 text-muted" />
                                                        {formatDate(purchase.purchase_date)}
                                                    </div>
                                                </td>
                                                <td>{purchase.supplier_name}</td>
                                                <td>
                                                    {purchase.invoice_no ? (
                                                        <code>{purchase.invoice_no}</code>
                                                    ) : (
                                                        <span className="text-muted">-</span>
                                                    )}
                                                </td>
                                                <td>{purchase.total_items || 0}</td>
                                                <td>{purchase.total_quantity || 0}</td>
                                                <td className="fw-bold text-success">{formatCurrency(purchase.total_value)}</td>
                                                <td className="text-center">
                                                    <button
                                                        className="btn btn-sm btn-outline-info"
                                                        onClick={() => handleViewPurchase(purchase)}
                                                        title="View Details"
                                                    >
                                                        <Eye size={14} />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {/* Pagination */}
                        {pagination.totalPages > 1 && (
                            <div className="d-flex justify-content-between align-items-center mt-3">
                                <div className="text-muted">
                                    Showing {((pagination.page - 1) * pagination.limit) + 1} to{' '}
                                    {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} entries
                                </div>
                                <nav>
                                    <ul className="pagination">
                                        <li className={`page-item ${pagination.page === 1 ? 'disabled' : ''}`}>
                                            <button className="page-link" onClick={() => loadPurchases(pagination.page - 1)}>
                                                Previous
                                            </button>
                                        </li>
                                        {[...Array(Math.min(5, pagination.totalPages))].map((_, i) => {
                                            let pageNum = pagination.page;
                                            if (pagination.totalPages <= 5) {
                                                pageNum = i + 1;
                                            } else if (pagination.page <= 3) {
                                                pageNum = i + 1;
                                            } else if (pagination.page >= pagination.totalPages - 2) {
                                                pageNum = pagination.totalPages - 4 + i;
                                            } else {
                                                pageNum = pagination.page - 2 + i;
                                            }
                                            return (
                                                <li key={pageNum} className={`page-item ${pagination.page === pageNum ? 'active' : ''}`}>
                                                    <button className="page-link" onClick={() => loadPurchases(pageNum)}>
                                                        {pageNum}
                                                    </button>
                                                </li>
                                            );
                                        })}
                                        <li className={`page-item ${pagination.page === pagination.totalPages ? 'disabled' : ''}`}>
                                            <button className="page-link" onClick={() => loadPurchases(pagination.page + 1)}>
                                                Next
                                            </button>
                                        </li>
                                    </ul>
                                </nav>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Create Purchase Modal */}
            {showCreateModal && (
                <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered modal-xl">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">
                                    <ShoppingCart size={18} className="me-2" />
                                    Purchase Raw Materials
                                </h5>
                                <button type="button" className="btn-close" onClick={() => setShowCreateModal(false)}></button>
                            </div>
                            <div className="modal-body">
                                {errors.form && (
                                    <div className="alert alert-danger alert-dismissible fade show" role="alert">
                                        <AlertCircle size={16} className="me-2" />
                                        {errors.form}
                                        <button type="button" className="btn-close" onClick={() => setErrors({})}></button>
                                    </div>
                                )}

                                <div className="row mb-4">
                                    <div className="col-md-4">
                                        <label className="form-label">Purchase Date *</label>
                                        <input
                                            type="date"
                                            name="purchase_date"
                                            className="form-control"
                                            value={formData.purchase_date}
                                            onChange={handleInputChange}
                                            required
                                        />
                                    </div>
                                    <div className="col-md-4">
                                        <label className="form-label">Supplier *</label>
                                        <Select
                                            options={supplierOptions}
                                            onChange={(selected) => handleSelectChange(selected, 'supplier_id')}
                                            placeholder="Select supplier"
                                            className={errors.supplier_id ? 'is-invalid' : ''}
                                        />
                                        {errors.supplier_id && <div className="text-danger small mt-1">{errors.supplier_id}</div>}
                                    </div>
                                    <div className="col-md-4">
                                        <label className="form-label">Invoice No</label>
                                        <input
                                            type="text"
                                            name="invoice_no"
                                            className="form-control"
                                            value={formData.invoice_no}
                                            onChange={handleInputChange}
                                            placeholder="Supplier invoice number"
                                        />
                                    </div>
                                    <div className="col-md-6 mt-3">
                                        <label className="form-label">Reference No (PO No)</label>
                                        <input
                                            type="text"
                                            name="reference_no"
                                            className="form-control"
                                            value={formData.reference_no}
                                            onChange={handleInputChange}
                                            placeholder="Your purchase order number"
                                        />
                                    </div>
                                    <div className="col-md-6 mt-3">
                                        <label className="form-label">Notes</label>
                                        <textarea
                                            name="notes"
                                            className="form-control"
                                            value={formData.notes}
                                            onChange={handleInputChange}
                                            rows="2"
                                            placeholder="Additional notes"
                                        />
                                    </div>
                                </div>

                                <div className="border-top pt-3">
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <h6 className="fw-bold">
                                            <Package size={16} className="me-2" />
                                            Raw Materials to Purchase
                                        </h6>
                                        <button type="button" className="btn btn-sm btn-primary" onClick={handleAddMaterial}>
                                            <Plus size={14} className="me-1" /> Add Raw Material
                                        </button>
                                    </div>

                                    {errors.items && <div className="alert alert-danger">{errors.items}</div>}

                                    <div className="table-responsive">
                                        <table className="table table-bordered">
                                            <thead className="table-light">
                                                <tr>
                                                    <th style={{ width: '35%' }}>Raw Material</th>
                                                    <th style={{ width: '12%' }}>Quantity</th>
                                                    <th style={{ width: '8%' }}>Unit</th>
                                                    <th style={{ width: '15%' }}>Unit Price (₹)</th>
                                                    <th style={{ width: '15%' }}>Total (₹)</th>
                                                    <th style={{ width: '10%' }}>Remarks</th>
                                                    <th style={{ width: '5%' }}>Action</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {formData.items.length === 0 ? (
                                                    <tr>
                                                        <td colSpan="7" className="text-center py-4 text-muted">
                                                            Click Add Raw Material  to add items to purchase
                                                        </td>
                                                    </tr>
                                                ) : (
                                                    formData.items.map((item, index) => (
                                                        <tr key={item.id}>
                                                            <td>
                                                                <Select
                                                                    options={rawMaterialOptions}
                                                                    onChange={(selected) => handleMaterialSelect(index, selected)}
                                                                    placeholder="Select raw material"
                                                                    className={errors[`material_${index}`] ? 'is-invalid' : ''}
                                                                    menuPortalTarget={document.body}
                                                                    menuPosition="fixed"
                                                                    styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                                                                />
                                                                {errors[`material_${index}`] && <div className="text-danger small mt-1">{errors[`material_${index}`]}</div>}
                                                            </td>
                                                            <td>
                                                                <input
                                                                    type="number"
                                                                    className={`form-control ${errors[`quantity_${index}`] ? 'is-invalid' : ''}`}
                                                                    value={item.quantity || ''}
                                                                    onChange={(e) => handleQuantityChange(index, e.target.value)}
                                                                    step="1"
                                                                    min="0"
                                                                    placeholder="Qty"
                                                                />
                                                                {errors[`quantity_${index}`] && <div className="text-danger small mt-1">{errors[`quantity_${index}`]}</div>}
                                                            </td>
                                                            <td>
                                                                <input
                                                                    type="text"
                                                                    className="form-control"
                                                                    value={item.unit}
                                                                    readOnly
                                                                />
                                                            </td>
                                                            <td>
                                                                <input
                                                                    type="number"
                                                                    className={`form-control ${errors[`price_${index}`] ? 'is-invalid' : ''}`}
                                                                    value={item.unit_price || ''}
                                                                    onChange={(e) => handleUnitPriceChange(index, e.target.value)}
                                                                    step="0.01"
                                                                    min="0"
                                                                    placeholder="Price"
                                                                />
                                                                {errors[`price_${index}`] && <div className="text-danger small mt-1">{errors[`price_${index}`]}</div>}
                                                            </td>
                                                            <td>
                                                                <input
                                                                    type="text"
                                                                    className="form-control bg-light"
                                                                    value={item.total_price.toFixed(2)}
                                                                    readOnly
                                                                />
                                                            </td>
                                                            <td>
                                                                <input
                                                                    type="text"
                                                                    className="form-control"
                                                                    value={item.remarks || ''}
                                                                    onChange={(e) => {
                                                                        const updatedItems = [...formData.items];
                                                                        updatedItems[index].remarks = e.target.value;
                                                                        setFormData(prev => ({ ...prev, items: updatedItems }));
                                                                    }}
                                                                    placeholder="Remarks"
                                                                />
                                                            </td>
                                                            <td className="text-center">
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-sm btn-danger"
                                                                    onClick={() => handleRemoveMaterial(index)}
                                                                >
                                                                    <Trash2 size={14} />
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    ))
                                                )}
                                            </tbody>
                                            {formData.items.length > 0 && (
                                                <tfoot className="table-light">
                                                    <tr>
                                                        <td colSpan="2" className="text-end fw-bold">Totals:</td>
                                                        <td className="fw-bold">{totals.totalItems} Items</td>
                                                        <td className="fw-bold">{totals.totalQuantity} Units</td>
                                                        <td className="fw-bold">₹{totals.totalValue.toFixed(2)}</td>
                                                        <td colSpan="2"></td>
                                                    </tr>
                                                </tfoot>
                                            )}
                                        </table>
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => setShowCreateModal(false)}>
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    onClick={handleSubmit}
                                    disabled={submitting}
                                >
                                    {submitting ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2"></span>
                                            Processing...
                                        </>
                                    ) : (
                                        <>
                                            <ShoppingCart size={16} className="me-2" />
                                            Purchase Raw Materials
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* View Purchase Modal */}
            {showViewModal && selectedPurchase && (
                <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered modal-lg">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">
                                    <ShoppingCart size={18} className="me-2" />
                                    Purchase Details - {selectedPurchase.po_no}
                                </h5>
                                <button type="button" className="btn-close" onClick={() => setShowViewModal(false)}></button>
                            </div>
                            <div className="modal-body">
                                {viewLoading ? (
                                    <div className="text-center py-5">
                                        <div className="spinner-border text-primary" role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        <div className="row mb-4">
                                            <div className="col-md-6">
                                                <div className="card bg-light">
                                                    <div className="card-body">
                                                        <h6 className="mb-3">Purchase Information</h6>
                                                        <p><strong>PO No:</strong> {selectedPurchase.po_no}</p>
                                                        <p><strong>Date:</strong> {formatDate(selectedPurchase.purchase_date)}</p>
                                                        <p><strong>Supplier:</strong> {selectedPurchase.supplier_name}</p>
                                                        {selectedPurchase.invoice_no && (
                                                            <p><strong>Invoice No:</strong> {selectedPurchase.invoice_no}</p>
                                                        )}
                                                        {selectedPurchase.reference_no && (
                                                            <p><strong>Reference No:</strong> {selectedPurchase.reference_no}</p>
                                                        )}
                                                        {selectedPurchase.notes && (
                                                            <p><strong>Notes:</strong> {selectedPurchase.notes}</p>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-md-6">
                                                <div className="card bg-light">
                                                    <div className="card-body">
                                                        <h6 className="mb-3">Summary</h6>
                                                        <p><strong>Total Items:</strong> {selectedPurchase.total_items || 0}</p>
                                                        <p><strong>Total Quantity:</strong> {selectedPurchase.total_quantity || 0}</p>
                                                        <p><strong>Total Value:</strong> {formatCurrency(selectedPurchase.total_value)}</p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <h6 className="fw-bold mb-3">Purchased Raw Materials</h6>
                                        <div className="table-responsive">
                                            <table className="table table-bordered">
                                                <thead className="table-light">
                                                    <tr>
                                                        <th>Raw Material</th>
                                                        <th>SKU</th>
                                                        <th>Quantity</th>
                                                        <th>Unit</th>
                                                        <th>Unit Price (₹)</th>
                                                        <th>Total (₹)</th>
                                                        <th>Remarks</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {selectedPurchase.items && selectedPurchase.items.length > 0 ? (
                                                        selectedPurchase.items.map((item, idx) => (
                                                            <tr key={idx}>
                                                                <td>
                                                                    <strong>{item.material_name}</strong>
                                                                </td>
                                                                <td><code>{item.sku}</code></td>
                                                                <td className="text-end">{parseFloat(item.quantity).toLocaleString('en-IN')}</td>
                                                                <td>{item.unit || 'PCS'}</td>
                                                                <td className="text-end">₹{parseFloat(item.unit_price).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                                                                <td className="text-end fw-bold text-success">
                                                                    ₹{parseFloat(item.total_price).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                                                </td>
                                                                <td>{item.remarks || '-'}</td>
                                                            </tr>
                                                        ))
                                                    ) : (
                                                        <tr>
                                                            <td colSpan="7" className="text-center py-4 text-muted">
                                                                <Package size={32} className="mb-2 d-block mx-auto" />
                                                                No items found in this purchase
                                                            </td>
                                                        </tr>
                                                    )}
                                                </tbody>
                                                {selectedPurchase.items && selectedPurchase.items.length > 0 && (
                                                    <tfoot className="table-light">
                                                        <tr>
                                                            <td colSpan="5" className="text-end fw-bold">Grand Total:</td>
                                                            <td className="text-end fw-bold text-success">
                                                                {formatCurrency(selectedPurchase.total_value)}
                                                            </td>
                                                            <td></td>
                                                        </tr>
                                                    </tfoot>
                                                )}
                                            </table>
                                        </div>
                                    </>
                                )}
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => setShowViewModal(false)}>
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default RawMaterialPurchase;